// ── Email Log Parser ──
// Parses Exchange message tracking logs (CSV) and Gmail audit email logs (JSON).
// Follows same patterns as parser.js for detection, parsing, normalization, and alias mapping.
// Pure functions, immutable operations, small functions.

// ── Format Detection ──

export function detectEmailLogFormat(content, filename) {
  const lower = (filename || '').toLowerCase();
  const first2k = content.slice(0, 2000).toLowerCase();

  // Filename-based detection (fast path)
  if (lower.includes('exchange') || lower.includes('message-track') || lower.includes('smtp') || lower.includes('trace')) {
    return 'exchange';
  }
  if (lower.includes('gmail') || lower.includes('google-workspace') || lower.includes('audit')) {
    return 'gmail-email';
  }

  // Content-based detection: Exchange CSV columns (multiple formats)
  const exchangeHyphenCols = ['date-time', 'client-ip', 'sender-address', 'recipient-address', 'message-subject'];
  const exchangeUnderscoreCols = ['date_time', 'client_ip', 'sender_address', 'recipient_address', 'message_subject'];
  const exchangeSpacedCols = ['date (utc)', 'client ip', 'sender address', 'recipient address', 'message subject'];
  const exchangeGeneric = ['original-client-ip', 'original_client_ip', 'recipient-status', 'recipient_status', 'connector-id', 'connector_id'];

  const hasAny = (cols) => cols.some(col => first2k.includes(col));
  if (hasAny(exchangeHyphenCols)) return 'exchange';
  if (hasAny(exchangeUnderscoreCols)) return 'exchange';
  if (hasAny(exchangeSpacedCols)) return 'exchange';
  if (hasAny(exchangeGeneric)) return 'exchange';

  // Broader: first line contains semicolons + exchange-like terms
  const firstLine = content.split('\n')[0]?.toLowerCase() || '';
  if (firstLine.includes(';') && (firstLine.includes('sender') || firstLine.includes('recipient') || firstLine.includes('client_ip'))) {
    return 'exchange';
  }

  // Content-based detection: Gmail JSON patterns
  const gmailPatterns = ['actor.email', '"events"', '"email"'];
  const gmailMatch = gmailPatterns.some(p => first2k.includes(p));
  if (gmailMatch) return 'gmail-email';

  return 'unknown';
}

// ── CSV Parsing (reused from parser.js pattern) ──

function parseCSVLine(line, separator) {
  const values = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && i + 1 < line.length && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === separator && !inQuotes) {
      values.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  values.push(current);
  return values;
}

function parseCSV(content) {
  const lines = content.trim().split('\n');
  if (lines.length < 2) return [];

  const firstLine = lines[0] || '';
  const separator = firstLine.includes('\t') ? '\t' : firstLine.includes(';') ? ';' : ',';
  const headers = firstLine.trim().split(separator).map(h => h.trim().replace(/^"|"$/g, ''));

  const results = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const values = parseCSVLine(line, separator);
    if (values.length === 0 || values.every(v => !v)) continue;

    const record = {};
    headers.forEach((h, idx) => {
      record[h] = values[idx] || '';
    });
    results.push(record);
  }

  return results;
}

// ── Exchange Message Tracking Log Parser ──

export function parseExchangeMessageTracking(content) {
  const rows = parseCSV(content);

  return rows.map(row => {
    // Normalize hyphenated header names to match alias map
    const record = {};
    for (const [key, value] of Object.entries(row)) {
      const normalizedKey = key.toLowerCase().replace(/[^a-z0-9_.-]/g, '');
      record[normalizedKey] = value;
    }
    return record;
  });
}

// ── Gmail Email Audit Log Parser ──

export function parseGmailEmailLog(content) {
  let jsonData;
  try {
    jsonData = JSON.parse(content);
  } catch {
    return [];
  }

  const records = Array.isArray(jsonData) ? jsonData : [jsonData];

  return records.flatMap(r => {
    const events = r.events || [];
    if (events.length === 0) return [];

    // Filter to email-related events
    const emailEvents = events.filter(e => {
      const name = (e.name || '').toLowerCase();
      return name.includes('email') || name.includes('message');
    });

    if (emailEvents.length === 0) return [];

    return emailEvents.map(event => {
      const params = event.parameters || [];
      const getParam = (name) => {
        const p = params.find(param => param.name === name);
        return p?.value || p?.boolValue || '';
      };

      return {
        timestamp: r.id?.time || '',
        'actor.email': r.actor?.email || '',
        ipAddress: r.ipAddress || '',
        event: event.name || '',
        destination_email: getParam('destination_email'),
        subject: getParam('subject'),
        message_id: getParam('message_id'),
        recipients: getParam('recipients') || getParam('destination_email') || '',
        raw: r,
      };
    });
  }).filter(r => r.ipAddress && r.ipAddress !== '');
}

// ── Email Record Normalization ──

const EMAIL_FIELD_MAP = {
  timestamp: [
    'timestamp', 'date-time', 'date_time', 'datetime',
    'date', 'time', 'date(utc)', 'dateutc',
  ],
  sender: [
    'sender', 'sender-address', 'sender_address', 'senderaddress',
    'sender address', 'from', 'from_address',
    'actor.email', 'actor',
  ],
  recipients: [
    'recipient', 'recipient-address', 'recipient_address', 'recipientaddress',
    'recipient address', 'recipients', 'to', 'destination_email',
  ],
  subject: [
    'subject', 'message-subject', 'message_subject', 'messagesubject',
    'message subject', 'email_subject',
  ],
  senderIp: [
    'senderip', 'sender-ip', 'sender_ip',
    'client-ip', 'client_ip', 'clientip',
    'client ip', 'clientaddress', 'client-address',
    'ip', 'ipaddress', 'ip_address',
    'original-client-ip', 'original-client_ip', 'originalclientip',
    'source-ip', 'source_ip', 'sourceip',
  ],
  messageId: [
    'message-id', 'message_id', 'messageid',
    'internal-message-id', 'internal-message_id', 'internalmessageid',
    'network-message-id', 'network-message_id',
  ],
  status: [
    'status', 'result', 'event',
    'recipient-status', 'recipient_status', 'recipientstatus',
  ],
};

function buildAliasMap(fieldMap) {
  const aliasMap = new Map();
  for (const [target, aliases] of Object.entries(fieldMap)) {
    for (const alias of aliases) {
      aliasMap.set(alias, target);
    }
  }
  return aliasMap;
}

const EMAIL_ALIAS_MAP = buildAliasMap(EMAIL_FIELD_MAP);

export function normalizeEmailRecords(records, source) {
  return records.map(record => {
    const normalized = { source };

    // Single pass over record keys with O(1) alias lookup
    for (const [key, value] of Object.entries(record)) {
      if (key === 'raw') continue;
      const keyLower = key.toLowerCase().replace(/[^a-z0-9_.-]/g, '');
      const target = EMAIL_ALIAS_MAP.get(keyLower);
      if (target && !(target in normalized)) {
        normalized[target] = value;
      }
    }

    // Fallback: scan all values for an IP pattern if senderIp not found
    if (!normalized.senderIp) {
      for (const v of Object.values(record)) {
        const s = String(v);
        if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(s.trim())) {
          normalized.senderIp = s.trim();
          break;
        }
      }
    }

    // Ensure all fields have defaults
    normalized.timestamp = normalized.timestamp || '';
    normalized.sender = normalized.sender || '';
    normalized.recipients = normalized.recipients || '';
    normalized.subject = normalized.subject || '';
    normalized.senderIp = normalized.senderIp || '';
    normalized.messageId = normalized.messageId || '';
    normalized.status = normalized.status || '';

    return normalized;
  }).filter(r => r.senderIp);
}

// ── Timestamp Processing ──

function processTimestamp(record) {
  let ts = record.timestamp ? new Date(record.timestamp) : null;
  if (!ts || isNaN(ts.getTime())) ts = null;
  return {
    ...record,
    timestamp: ts ? ts.toISOString() : new Date().toISOString(),
  };
}

// ── Main Entry Point ──

export async function parseEmailLogFile(file) {
  let content;
  try {
    content = await file.text();
  } catch (err) {
    throw new Error(`Failed to read file: ${err.message}`);
  }

  content = content.replace(/^\ufeff/, '');
  const source = detectEmailLogFormat(content, file.name);

  let records;

  if (source === 'exchange') {
    const parsed = parseExchangeMessageTracking(content);
    records = normalizeEmailRecords(parsed, source);
  } else if (source === 'gmail-email') {
    const parsed = parseGmailEmailLog(content);
    records = normalizeEmailRecords(parsed, source);
  } else {
    throw new Error(`Unrecognized email log format: ${source}`);
  }

  return records.map(record => ({
    ...processTimestamp(record),
    senderIp: record.senderIp.replace(/^::ffff:/, ''),
    status: record.status || 'unknown',
    source,
  }));
}
